import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: 'h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-xs': "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-8',
        'icon-lg': 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Legacy app extension: replaces children with a spinner while a mutation runs. */
    loading?: boolean;
  };

/**
 * Renders the button body, keeping the spinner separate from the slotted child.
 *
 * For `asChild`, the child must stay in place (via `Slot.Slottable`) so the
 * composed element keeps its semantics; the spinner is rendered alongside it.
 * For regular buttons, the spinner replaces the children (legacy behavior).
 *
 * @param params - Rendering context.
 * @param params.asChild - Whether the button composes a slotted child.
 * @param params.loading - Whether the loading spinner should be shown.
 * @param params.children - The button content.
 * @returns The button body nodes.
 */
function renderButtonContent(params: {
  asChild: boolean;
  loading: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  const { asChild, loading, children } = params;
  const spinner = <Loader2Icon className="animate-spin" />;

  if (asChild) {
    // Pass the slotted child straight through when there is no spinner to
    // render alongside it. Wrapping it in a fragment + `Slot.Slottable`
    // unconditionally breaks radix `Slot`'s prop-merge onto the child (e.g. a
    // react-router `<Link>`), leaving it an unstyled element. Only wrap when a
    // spinner sibling actually needs to be rendered.
    if (!loading) {
      return children;
    }
    return (
      <>
        {spinner}
        <Slot.Slottable>{children}</Slot.Slottable>
      </>
    );
  }

  return loading ? spinner : children;
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  loading = false,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
      disabled={loading || props.disabled}
    >
      {renderButtonContent({ asChild, loading, children })}
    </Comp>
  );
}

export { Button, buttonVariants };
